import { useMsal, } from '@azure/msal-react';
import { useParams } from "react-router-dom";
import { useEffect, useState, useMemo, createContext } from "react";
import PropertyAPI from "../../api/property/property-api-factory";
import { FileApi } from "../../api/file/file-api";
import { CompanyAPI } from '../../api/company/company-api-factory';
import _ from 'lodash';
import { Link } from "react-router-dom";
import LoadingAnimation from "../../components/loading/loading";
import { NewProject } from "../../components/kanban/new-project";
import KanbanAPI from "../../api/kanban-board/kanban-api-factory";
import { ImageUploadModal } from "../../components/files/image-upload-modal";
import { PropertyTypes } from "../../app/property-types";
import { PropertyTabsNav } from '../../components/properties/accordian-tabs-nav';
import { PropertyTabsContent } from '../../components/properties/property-tabs-content';
import { ImageEditModal } from '../../components/files/image-edit-modal';
import Comparer from '../../app/comparer';
import { DateTime } from 'luxon';
import { PropertyFunctions } from '../../api/property/property-functions';

export const PropertyContext = createContext();

export const PropertyDetail = () => {

    const { instance, inProgress, accounts } = useMsal();
    const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const companyAPI = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);

    const { id } = useParams();

    const [propertyDocument, setPropertyDocument] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [showNewProject, setShowNewProject] = useState(false);
    const [render, setRender] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedPropertyType, setSelectedPropertyType] = useState(null);
    const [showUploadImageModal, setShowUploadImageModal] = useState(false);
    const [showEditImageModal, setShowEditImageModal] = useState({ image: null, show: false });
    const [imageUploadProgress, setImageUploadProgress] = useState(null);
    const [imageUploadInProgress, setImageUploadInProgress] = useState(false);

    const api = useMemo(() => new PropertyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);
    const fileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    useEffect(() => {
        if (!fetchInProgress && !fetchComplete && !propertyDocument) {
            setFetchInProgress(true);
            api.get(id)
                .then(data => {
                    setPropertyDocument(data);
                    setFetchComplete(true);

                    if (data && data.property.propertyType) {
                        const types = PropertyTypes;
                        const propertyType = _.find(_.flatMap(types), t => t.id === data.property.propertyType);
                        setSelectedPropertyType(propertyType);
                    }
                })
                .finally(() => setFetchInProgress(false));
        }
    }, [api, id, fetchComplete, fetchInProgress, propertyDocument]);

    useEffect(() => {
        // Load single project if selectedProject exists
        if (propertyDocument?.property?.selectedProject && !selectedProject) {
            kanbanAPI.getProject(propertyDocument.property.selectedProject)
                .then(project => {
                    setSelectedProject(project);
                })
                .catch(error => {
                    console.error('Error loading project:', error);
                });
        }
    }, [kanbanAPI, propertyDocument, selectedProject]);

    const handleNewProjectClick = () => setShowNewProject(true);
    const handleCancelNewProject = () => setShowNewProject(false);
    const handleShowEditImageModal = (args) => setShowEditImageModal({ image: args.image, show: true });
    const handleCancelShowEditImageModal = () => setShowEditImageModal({ image: null, show: false });
    
    // locate an image inside any room's mediaGallery; returns { roomIndex, imageIndex } or null
    const findRoomImageLocation = (image, comparerInstance) => {
        const comparer = comparerInstance || new Comparer();
        const rooms = propertyDocument?.property?.rooms ?? [];
        for (let r = 0; r < rooms.length; r++) {
            const gallery = rooms[r]?.mediaGallery ?? [];
            const existingImageIndex = _.findIndex(gallery, p => comparer.equals(p, image));
            if (existingImageIndex !== -1) {
                return { roomIndex: r, imageIndex: existingImageIndex };
            }
        }
        return null;
    };

    const setImageStarRating = (imageInfo, rating) => {

        const comparer = new Comparer();
        const photoGallery = propertyDocument?.property?.photoGallery ?? [];
        const existingImageIndex = _.findIndex(photoGallery, p => comparer.equals(p, imageInfo));

        if (existingImageIndex !== -1) {
            // property-level gallery image
            const newVersion = structuredClone(photoGallery[existingImageIndex]);
            newVersion.rating = rating;

            newVersion.updated = DateTime.now().toUTC().toISO();
            newVersion.updatedBy = { userName: accounts[0].username, displayName: accounts[0].name, id: 'some_user_id' };

            propertyDocument.property.photoGallery.splice(existingImageIndex, 1); // Remove current version
            propertyDocument.property.photoGallery.splice(existingImageIndex, 0, newVersion); // Insert new version in its place

            setPropertyDocument(propertyDocument);
            saveProperty();
            setRender(!render);
            return;
        }

        // room-level media image
        const location = findRoomImageLocation(imageInfo, comparer);
        if (!location) return;

        const rooms = propertyDocument.property.rooms ?? [];
        const roomGallery = rooms[location.roomIndex].mediaGallery ?? [];
        const current = roomGallery[location.imageIndex];
        if (!current) return;

        const newVersion = structuredClone(current);
        newVersion.rating = rating;
        newVersion.updated = DateTime.now().toUTC().toISO();
        newVersion.updatedBy = { userName: accounts[0].username, displayName: accounts[0].name, id: 'some_user_id' };

        roomGallery.splice(location.imageIndex, 1);
        roomGallery.splice(location.imageIndex, 0, newVersion);

        setPropertyDocument(propertyDocument);
        saveProperty();
        setRender(!render);
    };

    const handleSetPropertyType = async type => {

        setSelectedPropertyType(type);

        propertyDocument.property.propertyType = type.id;

        await saveProperty();
    }

    const handleAddNewProject = async projectName => {
        const board = await kanbanAPI.addProject(projectName);
        setSelectedProject(board);

        // Update property document with new project
        setPropertyDocument(current => {
            current.property.selectedProject = board.id;
            return current;
        });

        setShowNewProject(false);
    };


    // TODO: need to split out the save function from updating the propertyDocument state
    // like we do in other places. This will allow us to implement change detection properly.
    // also will allow us to add validation before saving.
    // The state updates is good. Probably dont need to map every property like we do below.
    // We can just update the propertyDocument directly and call setPropertyDocument once.
    // That would update the state, which could then render a save button to perform the save.
    const handleSaveProperty = async data => {
        try {
            // Create updated document with form data
            const updatedDocument = structuredClone(propertyDocument);
            updatedDocument.property.name = data.name;
            updatedDocument.property.address.doorNumberOrName = data.address.doorNumberOrName;
            updatedDocument.property.address.roadName = data.address.roadName;
            updatedDocument.property.address.postalCode = data.address.postalCode;
            updatedDocument.property.address.town = data.address.town;
            updatedDocument.property.landlordOrOwner = data.landlordOrOwner;
            updatedDocument.property.landlordOrOwnerId = data.landlordOrOwner.id;
            updatedDocument.property.selectedProject = data.selectedProject;
            updatedDocument.property.rooms = data.rooms;
            if (Object.prototype.hasOwnProperty.call(data, 'description')) {
                updatedDocument.property.description = data.description;
            }

            // Save the updated document
            const result = await api.upsertProperty(updatedDocument);
            setPropertyDocument(result);

            // Load project if selectedProject exists
            if (result.property?.selectedProject && !selectedProject) {
                const project = await kanbanAPI.getProject(result.property.selectedProject);
                setSelectedProject(project);
            }

            return result;
        }
        catch (error) {
            console.error('Error saving property:', error);
            throw error;
        }
        finally {
            setRender(!render);
        }
    };

    const saveProperty = async (docToSave) => {
        try {
            const payload = docToSave ?? propertyDocument;
            const data = await api.upsertProperty(payload);
            setPropertyDocument(data);
            return data;
        } catch (error) {
            console.log("Error occurred upserting property", error);
            throw error;
        }
    };

    const handleCancelAddImages = () => setShowUploadImageModal(false);

    const addNewImagesToPhotoGallery = (collection, totalSize) => {

        const savePropertyCallback = async (doc) => {
            try {
                const response = await api.upsertProperty(doc);
                const companyId = response?.property?.landlordOrOwner?.parent?.id;

                if (!companyId) {
                    throw new Error('Unable to determine company id for file upload.');
                }

                const company = await companyAPI.get(companyId);

                await fileApi.uploadFiles(company.name, `${doc.id}/Gallery`, collection, (data) => {
                    const totalRemaining = _.sumBy(data, d => d.remainingBytes);
                    const progress = 1 - (totalRemaining / totalSize);
                    console.log(`Progress: ${progress * 100} %`);
                    setImageUploadProgress(progress);
                });

                setImageUploadInProgress(false);
                setImageUploadProgress(0);
                setPropertyDocument(response);
            }
            catch (error) {
                console.log("Error occurred uploading images.", error);
                setImageUploadInProgress(false);
            }
            finally {
                setShowUploadImageModal(false);
            }
        };

        PropertyFunctions.addImagesToProperty(propertyDocument, collection, savePropertyCallback);
    };

    const handleAddNewImages = (collection) => {
        console.log("Add new images...", collection);

        const totalSize = _.sumBy(collection, f => f.size);

        if (!imageUploadProgress) {
            setImageUploadInProgress(true);
            setImageUploadProgress(0);
            addNewImagesToPhotoGallery(collection, totalSize);
        }
    };

    const flipIsPrimaryFlags = (current, newVersion, existingImageIndex) => {

        if (current.isPrimaryImage !== newVersion.isPrimaryImage) {

            if (newVersion.isPrimaryImage) {

                for (let i = 0; i < propertyDocument.property.photoGallery.length; i++) {

                    if (i === existingImageIndex) { continue; }

                    const tempVersion = structuredClone(propertyDocument.property.photoGallery[i]);
                    tempVersion.isPrimaryImage = false;
                    propertyDocument.property.photoGallery.splice(i, 1); // Remove current version
                    propertyDocument.property.photoGallery.splice(i, 0, tempVersion); // Insert new version in its place
                }
            }
        }
    };

    const handleDeleteImageInfo = (imageInfo) => {
        const comparer = new Comparer();
        const photoGallery = propertyDocument?.property?.photoGallery ?? [];
        const existingImageIndex = _.findIndex(photoGallery, p => comparer.equals(p, imageInfo.image));

        if (existingImageIndex !== -1) {
            // property-level gallery image
            propertyDocument.property.photoGallery.splice(existingImageIndex, 1); // Remove current version

            setPropertyDocument(propertyDocument);
            saveProperty();
            handleCancelShowEditImageModal();
            setRender(!render);
            return;
        }

        // room-level media image
        const location = findRoomImageLocation(imageInfo.image, comparer);
        if (!location) return;

        const rooms = propertyDocument.property.rooms ?? [];
        const roomGallery = rooms[location.roomIndex].mediaGallery ?? [];
        roomGallery.splice(location.imageIndex, 1);

        setPropertyDocument(propertyDocument);
        saveProperty();
        handleCancelShowEditImageModal();
        setRender(!render);
    };

    const handleUpdateImageInfo = (imageInfo) => {

        const comparer = new Comparer();
        const photoGallery = propertyDocument?.property?.photoGallery ?? [];
        const existingImageIndex = _.findIndex(photoGallery, p => comparer.equals(p, imageInfo.image));

        if (existingImageIndex !== -1) {
            // property-level gallery image
            const newVersion = Object.assign(propertyDocument.property.photoGallery[existingImageIndex], imageInfo.newDetails);

            flipIsPrimaryFlags(imageInfo, newVersion, existingImageIndex);

            newVersion.updated = DateTime.now().toUTC().toISO();
            newVersion.updatedBy = { userName: accounts[0].username, displayName: accounts[0].name, id: 'some_user_id' };

            propertyDocument.property.photoGallery.splice(existingImageIndex, 1); // Remove current version
            propertyDocument.property.photoGallery.splice(existingImageIndex, 0, newVersion); // Insert new version in its place

            setPropertyDocument(propertyDocument);
            saveProperty();
            handleCancelShowEditImageModal();
            setRender(!render);
            return;
        }

        // room-level media image
        const location = findRoomImageLocation(imageInfo.image, comparer);
        if (!location) return;

        const rooms = propertyDocument.property.rooms ?? [];
        const roomGallery = rooms[location.roomIndex].mediaGallery ?? [];
        const current = roomGallery[location.imageIndex];
        if (!current) return;

        const newVersion = Object.assign({}, current, imageInfo.newDetails);

        newVersion.updated = DateTime.now().toUTC().toISO();
        newVersion.updatedBy = { userName: accounts[0].username, displayName: accounts[0].name, id: 'some_user_id' };

        // ensure only one primary image per room gallery, similar to photo gallery behaviour
        if (current.isPrimaryImage !== newVersion.isPrimaryImage && newVersion.isPrimaryImage) {
            for (let i = 0; i < roomGallery.length; i++) {
                if (i === location.imageIndex) continue;
                const tempVersion = structuredClone(roomGallery[i]);
                tempVersion.isPrimaryImage = false;
                roomGallery.splice(i, 1);
                roomGallery.splice(i, 0, tempVersion);
            }
        }

        roomGallery.splice(location.imageIndex, 1);
        roomGallery.splice(location.imageIndex, 0, newVersion);

        setPropertyDocument(propertyDocument);
        saveProperty();
        handleCancelShowEditImageModal();
        setRender(!render);
    };

    const isHMO = (propertyDoc) => propertyDoc && Object.hasOwn(propertyDoc.property, "propertyType") && propertyDoc.property.propertyType === PropertyTypes.HMO.id;

    const selectedProjectName = () => {
        return selectedProject ? selectedProject.name : "";
    };

    const saveDocs = async (newDocs) => {
        try {
            const updatedDocument = _.cloneDeep(propertyDocument);
            
            // Initialize documents array if it doesn't exist
            if (!updatedDocument.property.documents) {
                updatedDocument.property.documents = [];
            }
            
            updatedDocument.property.documents = _.filter(newDocs, d => d && !d.shouldDelete);
            const result = await api.upsertProperty(updatedDocument);
            setPropertyDocument(result);
            setRender(!render);
            
            return { documentHolder: result.property };
        } catch (error) {
            console.error('Error saving property documents:', error);
            throw error;
        }
    };

    const context = {
        saveDocs: saveDocs
    };

    return propertyDocument ? (
        <PropertyContext.Provider value={context}>
            <div>

            <NewProject
                show={showNewProject}
                handleCancel={handleCancelNewProject}
                handleSave={handleAddNewProject}
                defaultProjectName={propertyDocument.property.description}
            />

            <ImageUploadModal
                show={showUploadImageModal}
                handleCancel={handleCancelAddImages}
                handleSave={handleAddNewImages}
                imageUploadProgress={imageUploadProgress}
                imageUploadInProgress={imageUploadInProgress}
            />

            <ImageEditModal
                show={showEditImageModal.show}
                handleCancel={handleCancelShowEditImageModal}
                handleSave={handleUpdateImageInfo}
                imageInfo={showEditImageModal.image}
                handleDeleteImage={handleDeleteImageInfo}
            />

            <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/property/search">Property Search</Link></li>
                <li className="breadcrumb-item active">Details</li>
            </ol>
            <h1 className="page-header">Property Details | <small>{propertyDocument.property.address.doorNumberOrName} {propertyDocument.property.address.roadName}, {propertyDocument.property.address.postalCode} </small></h1>

            <PropertyTabsNav />

            <PropertyTabsContent
                propertyDocument={propertyDocument}
                selectedProject={selectedProject}
                handleNewProjectClick={handleNewProjectClick}
                handleSaveProperty={handleSaveProperty}
                selectedPropertyType={selectedPropertyType}
                handleSetPropertyType={handleSetPropertyType}
                isHMO={isHMO(propertyDocument)}
                selectedProjectName={selectedProjectName}
                photos={propertyDocument.property.photoGallery}
                setShowUploadImageModal={setShowUploadImageModal}
                setShowEditImageModal={handleShowEditImageModal}
                setStarRating={setImageStarRating}
                propertyApi={api}
            />
            </div>
        </PropertyContext.Provider>
    )
        :
        (<LoadingAnimation />);
}